import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useUserRole } from '../../../hooks/useUserRole';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import SalesOverviewService, { SalesOverview } from '../../sales/services/SalesOverviewService';
import BusinessPartnerSummaryService, { ContactsSummary, NewCustomerByMonth } from '../../contacts/services/BusinessPartnerSummaryService';
import EmployeeService from '../../employees/services/EmployeeService';
import CrmTaskService, { CrmTask } from '../../sales/services/CrmTaskService';
import AbsenceService, { Absence } from '../../absences/services/AbsenceService';
import ShiftService, { DayShifts, Shift } from '../../shifts/services/ShiftService';
import { Employee } from '../../../hooks/useEmployeeData';
import dateUtil from '../../../utils/dateUtil';
import { CHART_COLORS, CHART_SEMANTIC } from '../../../utils/chartColors';
import OfferFunnelChart from '../../sales/components/OfferFunnelChart';
import {
  FaPlus,
  FaCheckCircle,
  FaCheck,
  FaUsers,
  FaUserPlus,
  FaUserCheck,
  FaTasks,
  FaExclamationTriangle,
  FaChartLine,
  FaCalendarAlt,
  FaClock,
  FaExclamationCircle,
  FaArrowRight,
  FaRegCalendarCheck,
  FaUserClock,
  FaClipboardList,
} from 'react-icons/fa';
import EventsWidget from '../../events/components/EventsWidget';

interface EmployeeSummary {
  total: number;
  active: number;
  invited: number;
  waitingForManager: number;
  pendingDeactivation: number;
  deactivated: number;
}

interface ShiftSummary {
  totalShifts: number;
  totalNeeded: number;
  totalAssigned: number;
  occupancyPercent: number;
  criticalShifts: Array<{
    id: number;
    date: string;
    workArea: string;
    neededCount: number;
    assignedCount: number;
  }>;
}

interface TaskSummary {
  allTasks: CrmTask[];
  openTasks: CrmTask[];
  doneTasks: CrmTask[];
  overdueTasks: CrmTask[];
  totalOpen: number;
  totalInProgress: number;
  totalDone: number;
  totalOverdue: number;
}

interface AbsenceSummary {
  upcomingApproved: Absence[];
  pendingRequests: Absence[];
}

export default function Dashboard(): React.ReactElement {
  const { user, selectedTenant } = useAuthContext();
  const { role, workAreaIds, isAdmin } = useUserRole();
  const navigate = useNavigate();
  const [workAreaNames, setWorkAreaNames] = useState<string[]>([]);

  const [salesData, setSalesData] = useState<SalesOverview | null>(null);
  const [contactsData, setContactsData] = useState<ContactsSummary | null>(null);
  const [employeeData, setEmployeeData] = useState<EmployeeSummary | null>(null);
  const [taskData, setTaskData] = useState<TaskSummary | null>(null);
  const [absenceData, setAbsenceData] = useState<AbsenceSummary | null>(null);
  const [shiftData, setShiftData] = useState<ShiftSummary | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [taskTab, setTaskTab] = useState<'open' | 'done'>('open');
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (workAreaIds.length === 0) return;
    WorkAreaService.fetchWorkAreas()
      .then(allWAs => {
        const names = allWAs
          .filter(wa => workAreaIds.includes(wa.id))
          .map(wa => wa.name);
        setWorkAreaNames(names);
      })
      .catch(err => console.error('Failed to load work area names:', err));
  }, [workAreaIds]);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];

    const fetches: Promise<any>[] = [
      EmployeeService.fetchAllEmployees(),
      CrmTaskService.list(),
      AbsenceService.fetchAllAbsences(),
      ShiftService.fetchWeeklyShifts({ start: todayStr, days: 5 }),
      ...(isAdmin ? [
        SalesOverviewService.getOverview(),
        BusinessPartnerSummaryService.getSummary(),
      ] : []),
    ];

    Promise.allSettled(fetches).then(results => {
      const [employeesResult, tasksResult, absencesResult, shiftsResult, salesResult, contactsResult] = results;

      if (employeesResult.status === 'fulfilled') {
        const emps: Employee[] = Array.isArray(employeesResult.value) ? employeesResult.value : [];
        setEmployeeData({
          total: emps.length,
          active: emps.filter((e) => e.onboarding_status === 'ACTIVE').length,
          invited: emps.filter((e) => e.onboarding_status === 'INVITED').length,
          waitingForManager: emps.filter((e) => e.onboarding_status === 'WAITING_FOR_MANAGER').length,
          pendingDeactivation: emps.filter((e) => e.onboarding_status === 'PENDING_DEACTIVATION').length,
          deactivated: emps.filter((e) => e.onboarding_status === 'DEACTIVATED').length,
        });
      }

      if (tasksResult.status === 'fulfilled') {
        const allTasks: CrmTask[] = Array.isArray(tasksResult.value) ? tasksResult.value : [];
        const openTasks = allTasks
          .filter((t) => t.status === 'OPEN' || t.status === 'IN_PROGRESS')
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        const doneTasks = allTasks
          .filter((t) => t.status === 'DONE')
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        const overdueTasks = openTasks.filter((t) => CrmTaskService.isOverdue(t.due_date));
        setTaskData({
          allTasks, openTasks, doneTasks, overdueTasks,
          totalOpen: allTasks.filter((t) => t.status === 'OPEN').length,
          totalInProgress: allTasks.filter((t) => t.status === 'IN_PROGRESS').length,
          totalDone: doneTasks.length,
          totalOverdue: overdueTasks.length,
        });
      }

      if (absencesResult.status === 'fulfilled') {
        const allAbsences: Absence[] = Array.isArray(absencesResult.value) ? absencesResult.value : [];
        const now = new Date();
        const upcomingApproved = allAbsences
          .filter((a) => a.status === 'APPROVED' && dateUtil.createLocalDate(a.start_datetime) >= now)
          .sort((a, b) => dateUtil.createLocalDate(a.start_datetime).getTime() - dateUtil.createLocalDate(b.start_datetime).getTime())
          .slice(0, 5);
        const pendingRequests = allAbsences
          .filter((a) => a.status === 'REQUESTED')
          .sort((a, b) => dateUtil.createLocalDate(b.requested_on || b.start_datetime).getTime() - dateUtil.createLocalDate(a.requested_on || a.start_datetime).getTime())
          .slice(0, 5);
        setAbsenceData({ upcomingApproved, pendingRequests });
      }

      if (shiftsResult.status === 'fulfilled') {
        const dayShifts: DayShifts[] = Array.isArray(shiftsResult.value) ? shiftsResult.value : [];
        let totalShifts = 0;
        let totalNeeded = 0;
        let totalAssigned = 0;
        const criticalShifts: ShiftSummary['criticalShifts'] = [];
        dayShifts.forEach((day) => {
          (day.shifts || []).forEach((shift: Shift) => {
            totalShifts++;
            const needed = shift.employees_needed || 1;
            const assigned = shift.assigned_employees?.length || 0;
            totalNeeded += needed;
            totalAssigned += assigned;
            if (assigned < needed) {
              criticalShifts.push({ id: shift.id, date: day.date, workArea: shift.work_area__name || 'Unknown', neededCount: needed, assignedCount: assigned });
            }
          });
        });
        setShiftData({
          totalShifts, totalNeeded, totalAssigned,
          occupancyPercent: totalNeeded > 0 ? Math.round((totalAssigned / totalNeeded) * 100) : 100,
          criticalShifts: criticalShifts.slice(0, 5),
        });
      }

      if (salesResult?.status === 'fulfilled' && salesResult.value) {
        setSalesData(salesResult.value);
      }
      if (contactsResult?.status === 'fulfilled' && contactsResult.value) {
        setContactsData(contactsResult.value);
      }
    });
  }, [isAdmin]);

  const formatCurrency = (amount: number): string => {
    return SalesOverviewService.formatCurrency(amount);
  };

  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = (dateStr: string): string => {
    return dateUtil.createLocalDate(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  const getAbsenceTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      HOLIDAY: 'Urlaub',
      OVERTIME: 'Überstunden',
      SICK: 'Krank',
      SICK_CHILD: 'Kind krank',
      EXAM: 'Prüfung',
      WISH_OFF: 'Wunschfrei',
    };
    return labels[type] || type;
  };

  const userName = user?.first_name || 'User';
  const tenantName = selectedTenant?.tenant_name || 'Your Organization';
  const roleLabel = role === 'ADMIN' ? 'Admin' : role === 'MANAGER' ? 'Manager' : '';
  const roleSubtitle = [tenantName, roleLabel, ...workAreaNames].filter(Boolean).join(', ');

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      await CrmTaskService.create({ title: newTaskTitle.trim() });
      setNewTaskTitle('');
      // Reload tasks
      const allTasks = await CrmTaskService.list();
      const openTasks = allTasks
        .filter((t) => t.status === 'OPEN' || t.status === 'IN_PROGRESS')
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      const doneTasks = allTasks
        .filter((t) => t.status === 'DONE')
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      const overdueTasks = openTasks.filter((t) => CrmTaskService.isOverdue(t.due_date));
      setTaskData({
        allTasks,
        openTasks,
        doneTasks,
        overdueTasks,
        totalOpen: allTasks.filter((t) => t.status === 'OPEN').length,
        totalInProgress: allTasks.filter((t) => t.status === 'IN_PROGRESS').length,
        totalDone: doneTasks.length,
        totalOverdue: overdueTasks.length,
      });
    } catch (err) {
      console.error('Failed to create task:', err);
    }
  };

  const handleCompleteTask = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCompletingTaskId(taskId);
    try {
      await CrmTaskService.changeStatus(taskId, 'DONE');
      // Reload tasks
      const allTasks = await CrmTaskService.list();
      const openTasks = allTasks
        .filter((t) => t.status === 'OPEN' || t.status === 'IN_PROGRESS')
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      const doneTasks = allTasks
        .filter((t) => t.status === 'DONE')
        .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
      const overdueTasks = openTasks.filter((t) => CrmTaskService.isOverdue(t.due_date));
      setTaskData({
        allTasks,
        openTasks,
        doneTasks,
        overdueTasks,
        totalOpen: allTasks.filter((t) => t.status === 'OPEN').length,
        totalInProgress: allTasks.filter((t) => t.status === 'IN_PROGRESS').length,
        totalDone: doneTasks.length,
        totalOverdue: overdueTasks.length,
      });
    } catch (err) {
      console.error('Failed to complete task:', err);
    } finally {
      setCompletingTaskId(null);
    }
  };

  const customerHistory: Array<{ label: string; value: number }> = (contactsData?.customers.new_by_month || [])
    .slice(-6)
    .map((m: NewCustomerByMonth) => ({
      label: BusinessPartnerSummaryService.formatMonthLabel(m.month),
      value: m.count,
    }));

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', background: 'transparent', minHeight: '100vh' }}>
      {/* Greeting Header */}
      <div
        className="glass-header"
        style={{
          marginBottom: 24,
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: CHART_COLORS.navy }}>
            {getGreeting()}, {userName}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
            {roleSubtitle}
          </p>
        </div>
      </div>

      {/* Top Row: Tasks (left) + Personnel (right) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Tasks Section */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaTasks size={20} color={CHART_COLORS.navy} />
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Tasks</h2>
            </div>
            <button
              onClick={() => navigate('/operations')}
              className="glass-btn glass-btn-secondary"
              style={{ fontSize: 12 }}
            >
              View All
            </button>
          </div>

          {/* Donut Chart + Stats */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
            {/* Mini Donut */}
            <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
              <svg width={100} height={100} viewBox="0 0 100 100">
                {(() => {
                  const done = taskData?.totalDone || 0;
                  const overdue = taskData?.totalOverdue || 0;
                  const inProgress = taskData?.totalInProgress || 0;
                  const open = taskData?.totalOpen || 0;
                  const total = done + overdue + inProgress + open;

                  if (total === 0) {
                    return <circle cx={50} cy={50} r={40} fill="none" stroke="#e5e7eb" strokeWidth={12} />;
                  }

                  const segments = [
                    { value: done, color: CHART_COLORS.success },
                    { value: overdue, color: CHART_COLORS.pink },
                    { value: inProgress, color: CHART_COLORS.yellow },
                    { value: open, color: CHART_COLORS.blue },
                  ];

                  let currentAngle = -90;
                  const radius = 40;
                  const circumference = 2 * Math.PI * radius;

                  return segments.map((seg, idx) => {
                    if (seg.value === 0) return null;
                    const percent = (seg.value / total) * 100;
                    const dashLength = (percent / 100) * circumference;
                    const dashOffset = -((currentAngle + 90) / 360) * circumference;
                    currentAngle += (percent / 100) * 360;

                    return (
                      <circle
                        key={idx}
                        cx={50}
                        cy={50}
                        r={radius}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth={12}
                        strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                        strokeDashoffset={dashOffset}
                        style={{ transition: 'all 0.3s' }}
                      />
                    );
                  });
                })()}
              </svg>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: CHART_COLORS.navy }}>
                  {(taskData?.totalOpen || 0) + (taskData?.totalInProgress || 0) + (taskData?.totalOverdue || 0)}
                </div>
                <div style={{ fontSize: 10, color: '#888' }}>Open</div>
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS.success }} />
                <span style={{ fontSize: 12, color: '#666' }}>Done</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: CHART_COLORS.navy, marginLeft: 'auto' }}>{taskData?.totalDone || 0}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS.pink }} />
                <span style={{ fontSize: 12, color: '#666' }}>Overdue</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: CHART_COLORS.navy, marginLeft: 'auto' }}>{taskData?.totalOverdue || 0}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS.yellow }} />
                <span style={{ fontSize: 12, color: '#666' }}>In Progress</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: CHART_COLORS.navy, marginLeft: 'auto' }}>{taskData?.totalInProgress || 0}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS.blue }} />
                <span style={{ fontSize: 12, color: '#666' }}>Open</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: CHART_COLORS.navy, marginLeft: 'auto' }}>{taskData?.totalOpen || 0}</span>
              </div>
            </div>
          </div>

          {/* New Task Input */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                borderRadius: 8,
                border: '1px dashed rgba(0, 0, 0, 0.12)',
              }}
            >
              <FaPlus size={12} color="var(--color-petrol)" />
              <input
                type="text"
                placeholder="New task title..."
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateTask();
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: 14,
                  fontWeight: 600,
                  color: CHART_COLORS.navy,
                  outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setTaskTab('open')}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: taskTab === 'open' ? CHART_COLORS.navy : '#888',
                background: 'none',
                border: 'none',
                borderBottom: taskTab === 'open' ? `2px solid ${CHART_COLORS.navy}` : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              Open Tasks
            </button>
            <button
              onClick={() => setTaskTab('done')}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: taskTab === 'done' ? CHART_COLORS.navy : '#888',
                background: 'none',
                border: 'none',
                borderBottom: taskTab === 'done' ? `2px solid ${CHART_COLORS.navy}` : '2px solid transparent',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              Done
            </button>
          </div>

          {/* Task List */}
          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 200, overflowY: 'auto' }}>
            {taskTab === 'open' ? (
              (taskData?.openTasks.length || 0) === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
                  <FaCheckCircle size={16} style={{ marginBottom: 6, display: 'block', margin: '0 auto 6px' }} />
                  No open tasks
                </div>
              ) : (
                taskData?.openTasks.slice(0, 8).map((task) => {
                  const isOverdue = CrmTaskService.isOverdue(task.due_date);
                  const isCompleting = completingTaskId === task.id;

                  return (
                    <div
                      key={task.id}
                      onClick={() => navigate('/operations')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Checkbox Circle */}
                      <button
                        onClick={(e) => handleCompleteTask(task.id, e)}
                        disabled={isCompleting}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: `2px solid ${isOverdue ? CHART_COLORS.pink : '#d1d5db'}`,
                          background: 'white',
                          cursor: isCompleting ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#d1fae5';
                          e.currentTarget.style.borderColor = CHART_COLORS.success;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.borderColor = isOverdue ? CHART_COLORS.pink : '#d1d5db';
                        }}
                        title="Mark as done"
                      >
                        <FaCheck size={10} color={CHART_COLORS.success} style={{ opacity: 0 }} />
                      </button>

                      {/* Task Title */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: CHART_COLORS.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {task.title}
                        </div>
                      </div>

                      {/* Due Date */}
                      {task.due_date && (
                        <span style={{ fontSize: 12, fontWeight: 500, color: isOverdue ? CHART_COLORS.pink : '#888', flexShrink: 0 }}>
                          {isOverdue && <FaExclamationCircle size={10} style={{ marginRight: 4 }} />}
                          {CrmTaskService.formatDueDate(task.due_date)}
                        </span>
                      )}
                    </div>
                  );
                })
              )
            ) : (
              (taskData?.doneTasks.length || 0) === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>
                  No completed tasks
                </div>
              ) : (
                taskData?.doneTasks.slice(0, 8).map((task) => (
                  <div
                    key={task.id}
                    onClick={() => navigate('/operations')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <FaCheckCircle size={18} color={CHART_COLORS.success} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: 'line-through' }}>
                        {task.title}
                      </div>
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>

        {/* Personnel Section */}
        <div className="glass-card" style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaUsers size={20} color={CHART_COLORS.navy} />
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Personnel Overview</h2>
            </div>
            <button
              onClick={() => navigate('/employees')}
              className="glass-btn glass-btn-secondary"
              style={{ fontSize: 12 }}
            >
              View All
            </button>
          </div>

          {/* Personnel Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {/* Shift Coverage */}
            <div style={{ background: 'transparent', borderRadius: 8, padding: 14, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FaCalendarAlt size={14} color={CHART_COLORS.purple} />
                <span style={{ fontSize: 12, color: '#666' }}>Shift Coverage (5 Days)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, background: '#e5e7eb', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${shiftData?.occupancyPercent || 0}%`,
                      height: '100%',
                      background: (shiftData?.occupancyPercent || 0) >= 80 ? CHART_COLORS.success : (shiftData?.occupancyPercent || 0) >= 50 ? CHART_COLORS.yellow : CHART_COLORS.danger,
                      borderRadius: 6,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <span style={{ fontSize: 16, fontWeight: 700, color: CHART_COLORS.navy }}>{shiftData?.occupancyPercent || 0}%</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                {shiftData?.totalAssigned || 0} / {shiftData?.totalNeeded || 0} assigned
              </div>
            </div>
            {/* Onboarded */}
            <div style={{ background: 'transparent', borderRadius: 8, padding: 14, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaUserCheck size={14} color={CHART_COLORS.success} />
                <span style={{ fontSize: 12, color: '#666' }}>Onboarded</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: CHART_COLORS.success, marginTop: 6 }}>
                {employeeData?.active || 0}
                <span style={{ fontSize: 14, fontWeight: 500 }}> / {employeeData?.total || 0}</span>
              </div>
            </div>
          </div>

          {/* Critical Shifts */}
          {(shiftData?.criticalShifts.length || 0) > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                <FaExclamationTriangle size={12} color={CHART_COLORS.yellow} />
                Unfilled Shifts
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 140, overflowY: 'auto' }}>
                {shiftData?.criticalShifts.slice(0, 5).map((shift) => (
                  <div
                    key={shift.id}
                    onClick={() => navigate('/shiftplan')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      background: 'transparent',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ color: '#666' }}>
                      {formatDate(shift.date)} - {shift.workArea}
                    </span>
                    <span style={{ fontWeight: 600, color: CHART_COLORS.navy }}>
                      {shift.assignedCount}/ {shift.neededCount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Absences Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Upcoming Absences */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                <FaRegCalendarCheck size={12} />
                Upcoming Absences
              </div>
              {absenceData?.upcomingApproved.length === 0 ? (
                <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>None scheduled</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
                  {absenceData?.upcomingApproved.map((absence) => (
                    <div key={absence.id} style={{ fontSize: 11, padding: '6px 8px', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 500, color: CHART_COLORS.navy }}>{absence.employee_name || `Employee ${absence.employee}`}</span>
                      <span style={{ color: '#888' }}>{formatDate(absence.start_datetime)}-{formatDate(absence.end_datetime)}</span>
                      <span style={{ color: '#666', marginLeft: 'auto' }}>{getAbsenceTypeLabel(absence.absence_type)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Requests */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 8 }}>
                <FaUserClock size={12} />
                Pending Requests
                {(absenceData?.pendingRequests.length || 0) > 0 && (
                  <span
                    style={{
                      background: CHART_COLORS.yellow,
                      color: '#92400e',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: 10,
                    }}
                  >
                    {absenceData?.pendingRequests.length}
                  </span>
                )}
              </div>
              {absenceData?.pendingRequests.length === 0 ? (
                <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>No pending requests</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
                  {absenceData?.pendingRequests.map((absence) => (
                    <div
                      key={absence.id}
                      onClick={() => navigate('/absences')}
                      style={{ fontSize: 11, padding: '6px 8px', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span style={{ fontWeight: 500, color: CHART_COLORS.navy }}>{absence.employee_name || `Employee ${absence.employee}`}</span>
                      <span style={{ color: '#888' }}>{formatDate(absence.start_datetime)}-{formatDate(absence.end_datetime)}</span>
                      <span style={{ color: '#666', marginLeft: 'auto' }}>{getAbsenceTypeLabel(absence.absence_type)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Events Widget */}
      <div style={{ marginBottom: 24 }}>
        <EventsWidget />
      </div>

      {/* Contacts Section */}
      {isAdmin && <div className="glass-card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaUsers size={20} color={CHART_COLORS.navy} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Contacts</h2>
          </div>
          <button
            onClick={() => navigate('/contacts')}
            className="glass-btn glass-btn-secondary"
            style={{ fontSize: 12 }}
          >
            View All
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div style={{ background: 'transparent', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaUsers size={14} color={CHART_SEMANTIC.customer} />
              <span style={{ fontSize: 12, color: '#666' }}>Total Customers</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: CHART_COLORS.navy }}>{contactsData?.counts.CUSTOMER || 0}</div>
          </div>
          <div style={{ background: 'transparent', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaUserPlus size={14} color={CHART_COLORS.success} />
              <span style={{ fontSize: 12, color: '#666' }}>New This Month</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: CHART_COLORS.success }}>{contactsData?.customers.new_this_month || 0}</div>
          </div>
          <div style={{ background: 'transparent', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaClock size={14} color={CHART_COLORS.purple} />
              <span style={{ fontSize: 12, color: '#666' }}>Leads</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: CHART_COLORS.navy }}>{contactsData?.stages.LEAD || 0}</div>
          </div>
          <div style={{ background: 'transparent', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <FaClipboardList size={14} color={CHART_COLORS.blue} />
              <span style={{ fontSize: 12, color: '#666' }}>Active Projects</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: CHART_COLORS.navy }}>{salesData?.projects.counts.ACTIVE || 0}</div>
          </div>
        </div>

        {/* Mini Bar Chart for New Customers */}
        {customerHistory.length > 0 && (
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
            {customerHistory.map((item, idx) => {
              const maxVal = Math.max(...customerHistory.map((h) => h.value), 1);
              const height = Math.round((item.value / maxVal) * 100);
              return (
                <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '70%',
                      height: `${height}%`,
                      minHeight: item.value > 0 ? 4 : 0,
                      background: CHART_SEMANTIC.customer,
                      borderRadius: 3,
                    }}
                    title={`${item.label}: ${item.value}`}
                  />
                  <span style={{ fontSize: 10, color: '#888', marginTop: 4 }}>{item.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* Sales Section */}
      {isAdmin && <div className="glass-card" style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaChartLine size={20} color={CHART_COLORS.navy} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: CHART_COLORS.navy }}>Sales</h2>
          </div>
          <button
            onClick={() => navigate('/sales')}
            className="glass-btn glass-btn-secondary"
            style={{ fontSize: 12 }}
          >
            View All
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Offer Funnel */}
          {salesData && (
            <OfferFunnelChart
              counts={salesData.offers?.counts ?? { DRAFT: 0, SENT: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0 }}
              amounts={salesData.offers?.amounts}
              conversionRate={salesData.offers?.conversion_rate}
            />
          )}

          {/* Monthly Revenue Overview */}
          <div
            className="glass-panel"
            style={{
              padding: '20px 24px',
              height: '100%',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: CHART_COLORS.navy, marginBottom: 16 }}>
              Monthly Revenue
            </div>

            {/* Current Month Highlight */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 14, border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: 11, color: '#166534', marginBottom: 4, fontWeight: 500 }}>Invoiced this month</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#166534' }}>
                  {formatCurrency(salesData?.invoices?.month?.current?.invoiced_gross || 0)}
                </div>
              </div>
              <div style={{ background: '#eff6ff', borderRadius: 8, padding: 14, border: '1px solid #bfdbfe' }}>
                <div style={{ fontSize: 11, color: '#1e40af', marginBottom: 4, fontWeight: 500 }}>Paid this month</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1e40af' }}>
                  {formatCurrency(salesData?.invoices?.month?.current?.paid_gross || 0)}
                </div>
              </div>
            </div>

            {/* 12 Month Bar Chart */}
            {salesData?.invoices?.history && salesData.invoices.history.length > 0 && (() => {
              const displayData = salesData.invoices.history.slice(-12);
              const maxValue = Math.max(...displayData.map((p) => Math.max(p.invoicedGross, p.paidGross)), 1);
              const chartHeight = 100;

              const formatMonthLabel = (month: string): string => {
                const [, m] = month.split('-');
                const monthNames = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
                return monthNames[parseInt(m, 10) - 1];
              };

              return (
                <>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_SEMANTIC.invoiced }} />
                      <span style={{ fontSize: 11, color: '#666' }}>Invoiced</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_SEMANTIC.paid }} />
                      <span style={{ fontSize: 11, color: '#666' }}>Paid</span>
                    </div>
                  </div>

                  {/* Chart */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: chartHeight, borderBottom: '1px solid #e5e7eb' }}>
                    {displayData.map((point) => {
                      const invoicedHeight = (point.invoicedGross / maxValue) * (chartHeight - 10);
                      const paidHeight = (point.paidGross / maxValue) * (chartHeight - 10);
                      return (
                        <div
                          key={point.month}
                          style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 2, position: 'relative' }}
                          title={`${point.month}\nInvoiced: ${formatCurrency(point.invoicedGross)}\nPaid: ${formatCurrency(point.paidGross)}`}
                        >
                          <div
                            style={{
                              flex: 1,
                              height: Math.max(invoicedHeight, 2),
                              background: CHART_SEMANTIC.invoiced,
                              borderRadius: '2px 2px 0 0',
                            }}
                          />
                          <div
                            style={{
                              flex: 1,
                              height: Math.max(paidHeight, 2),
                              background: CHART_SEMANTIC.paid,
                              borderRadius: '2px 2px 0 0',
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* X-axis labels */}
                  <div style={{ display: 'flex', gap: 4, paddingTop: 4 }}>
                    {displayData.map((point) => (
                      <div key={point.month} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#888' }}>
                        {formatMonthLabel(point.month)}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>}
    </div>
  );
}
